import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Container,
  Stack,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Business as BusinessIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  MoreVert as MoreVertIcon,
  LocationOn as LocationIcon,
  Build as BuildIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { contractorsApi } from '../../services/api';
import CreateContractorModal from './CreateContractorModal';
import EditContractorModal from './EditContractorModal';
import ContractorStaffModal from './ContractorStaffModal';
import ContractorCapabilitiesModal from './ContractorCapabilitiesModal';

const ContractorListPage = () => {
  const { user, canAccess, getUserOrganization } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [capabilitiesModalOpen, setCapabilitiesModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuCompany, setMenuCompany] = useState(null);

  // All hooks must be called before any conditional returns
  useEffect(() => {
    // Only fetch if user has access
    if (canAccess(['manager'])) {
      fetchCompanies();
    }
  }, [canAccess]);

  // Check if user has manager access - moved after hooks
  if (!canAccess(['manager'])) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="error">
          Access denied. Only managers can access contractor management.
        </Alert>
      </Container>
    );
  }

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Fetching contractor companies...');
      const response = await contractorsApi.getCompanies();
      console.log('API Response:', response);
      
      // The API returns { success: true, count: X, data: [...] }
      // So we need to access response.data.data to get the actual companies array
      const companiesData = Array.isArray(response.data.data) ? response.data.data : [];
      console.log('Companies data:', companiesData);
      setCompanies(companiesData);
    } catch (error) {
      console.error('Failed to fetch contractor companies:', error);
      console.error('Error details:', error.response?.data || error.message);
      setError(`Failed to load contractor companies: ${error.response?.data?.message || error.message}`);
      // Ensure companies stays as an empty array on error
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = () => {
    setCreateModalOpen(true);
  };

  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    setEditModalOpen(true);
    handleCloseMenu();
  };

  const handleManageStaff = (company) => {
    setSelectedCompany(company);
    setStaffModalOpen(true);
    handleCloseMenu();
  };

  const handleManageCapabilities = (company) => {
    setSelectedCompany(company);
    setCapabilitiesModalOpen(true);
    handleCloseMenu();
  };

  const handleDeleteCompany = (company) => {
    setCompanyToDelete(company);
    setDeleteDialogOpen(true);
    handleCloseMenu();
  };

  const confirmDeleteCompany = async () => {
    try {
      await contractorsApi.deleteCompany(companyToDelete.id);
      setDeleteDialogOpen(false);
      setCompanyToDelete(null);
      await fetchCompanies(); // Refresh the list
    } catch (error) {
      console.error('Failed to delete contractor company:', error);
      setError('Failed to delete contractor company. Please try again.');
    }
  };

  const handleMenuOpen = (event, company) => {
    setAnchorEl(event.currentTarget);
    setMenuCompany(company);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuCompany(null);
  };

  const handleModalClose = () => {
    setCreateModalOpen(false);
    setEditModalOpen(false);
    setStaffModalOpen(false);
    setCapabilitiesModalOpen(false);
    setSelectedCompany(null);
  };

  const handleCompanyCreated = () => {
    handleModalClose();
    fetchCompanies();
  };

  const handleCompanyUpdated = () => {
    handleModalClose();
    fetchCompanies();
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={40} sx={{ mr: 2 }} />
        <Typography variant="body1" color="text.secondary">
          Loading contractor companies...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Page Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <BusinessIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Contractor Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage external contractor companies and their capabilities
            {user?.organizationName && ` - ${user.organizationName}`}
          </Typography>
        </Box>
      </Stack>

      {/* Action Header */}
      <Paper sx={{ p: 3, borderRadius: 2, mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          justifyContent="space-between" 
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ color: 'text.primary' }}>
              Contractor Companies
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total: {Array.isArray(companies) ? companies.length : 0} companies
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateCompany}
            size="large"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              boxShadow: '0 3px 10px rgba(25, 118, 210, 0.3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
              }
            }}
          >
            Add Contractor Company
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Contractors List */}
      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <BusinessIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Companies ({Array.isArray(companies) ? companies.length : 0})
          </Typography>
        </Stack>

        {!Array.isArray(companies) || companies.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No contractor companies found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
              Create your first contractor company to start managing external contractors and work assignments.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateCompany}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Add First Contractor
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {Array.isArray(companies) && companies.map((company) => (
              <Grid item xs={12} md={6} lg={4} key={company.id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box 
                          sx={{ 
                            p: 1.5, 
                            bgcolor: 'primary.light', 
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <BusinessIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                        </Box>
                        <Box>
                          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                            {company.name}
                          </Typography>
                          <Chip
                            label={company.is_active ? 'Active' : 'Inactive'}
                            color={company.is_active ? 'success' : 'default'}
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                      </Stack>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, company)}
                        sx={{ color: 'text.secondary' }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Stack>

                    <Stack spacing={1.5} sx={{ mb: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                          Contact Information
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {company.contact_person || 'Not specified'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {company.email || 'No email'} • {company.phone || 'No phone'}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                          Capabilities & Coverage
                        </Typography>
                        <Stack spacing={1} sx={{ mt: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <BuildIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {company.capability_count || 0} capabilities
                            </Typography>
                          </Stack>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <LocationIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {company.jurisdiction_count || 0} jurisdictions
                            </Typography>
                          </Stack>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <PeopleIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {company.staff_count || 0} staff members
                            </Typography>
                          </Stack>
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0, justifyContent: 'space-between' }}>
                    <Button
                      size="small"
                      startIcon={<SettingsIcon />}
                      onClick={() => handleManageCapabilities(company)}
                      sx={{ 
                        textTransform: 'none',
                        fontWeight: 500,
                        borderRadius: 1.5
                      }}
                    >
                      Capabilities
                    </Button>
                    <Button
                      size="small"
                      startIcon={<PeopleIcon />}
                      onClick={() => handleManageStaff(company)}
                      sx={{ 
                        textTransform: 'none',
                        fontWeight: 500,
                        borderRadius: 1.5
                      }}
                    >
                      Staff
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          elevation: 8,
          sx: {
            borderRadius: 2,
            minWidth: 200
          }
        }}
      >
        <MenuItem onClick={() => handleEditCompany(menuCompany)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Company</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleManageCapabilities(menuCompany)}>
          <ListItemIcon>
            <BuildIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Manage Capabilities</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleManageStaff(menuCompany)}>
          <ListItemIcon>
            <PeopleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Manage Staff</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDeleteCompany(menuCompany)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Company</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{companyToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteCompany} 
            color="error" 
            variant="contained"
            sx={{ textTransform: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modals */}
      <CreateContractorModal
        open={createModalOpen}
        onClose={handleModalClose}
        onCompanyCreated={handleCompanyCreated}
      />

      <EditContractorModal
        open={editModalOpen}
        company={selectedCompany}
        onClose={handleModalClose}
        onCompanyUpdated={handleCompanyUpdated}
      />

      <ContractorStaffModal
        open={staffModalOpen}
        company={selectedCompany}
        onClose={handleModalClose}
      />

      <ContractorCapabilitiesModal
        open={capabilitiesModalOpen}
        company={selectedCompany}
        onClose={handleModalClose}
        onCapabilitiesUpdated={fetchCompanies}
      />
    </Container>
  );
};

export default ContractorListPage; 